import React, { useEffect, useState, useCallback } from 'react';
import { Input, Select, message, Tag, Drawer, Descriptions, Typography, theme } from 'antd';
import { Link } from 'react-router-dom';
import { getAuditLog, AuditLogEntry } from '../../services/adminService';
import PageHelp from '../../components/PageHelp';
import PageHeader from '../../components/PageHeader';
import ResponsiveTableOrCards from '../../components/ResponsiveTableOrCards';
import AuditLogCard from '../../components/AuditLogCard';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Row, Col } from 'antd';

const { Search } = Input;
const { Text } = Typography;
const { useToken } = theme;

const PAGE_SIZE = 50;

const actionTagColor = (action: string): string => {
  if (action.startsWith('create') || action === 'allocate') return 'green';
  if (action.startsWith('update') || action === 'assign') return 'blue';
  if (action.startsWith('delete') || action === 'deallocate' || action === 'unassign') return 'red';
  if (action === 'login') return 'purple';
  if (action.startsWith('view')) return 'default';
  if (action === 'set_sim_visibility') return 'gold';
  return 'default';
};

const formatTimestamp = (val: string): string => {
  const d = new Date(val);
  return (
    d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) +
    ' ' +
    d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  );
};

const renderResourceLink = (
  resourceType: string | null,
  resourceId: string | null
): React.ReactNode => {
  if (!resourceType || !resourceId) return '-';

  const label = `${resourceType} #${resourceId}`;

  switch (resourceType) {
    case 'organization':
      return <Link to={`/admin/organizations/${resourceId}`}>{label}</Link>;
    case 'user':
      return <Link to="/admin/users">{label}</Link>;
    default:
      return <Text>{label}</Text>;
  }
};

const AuditLog: React.FC = () => {
  const { token } = useToken();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState<string | undefined>(undefined);
  const [searchText, setSearchText] = useState('');
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);

  const fetchAuditLog = useCallback(async (page: number, action?: string) => {
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const data = await getAuditLog({ action, limit: PAGE_SIZE, offset });
      setEntries(data.entries);
      setTotal(data.pagination.total);
    } catch (err: any) {
      if (err.status === 404 || err.message?.includes('Not Found')) {
        setEntries([]);
        setTotal(0);
        message.info('Audit log endpoint is not yet available');
      } else {
        message.error(err.message || 'Failed to load audit log');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchAuditLog(1, actionFilter);
  }, [actionFilter, fetchAuditLog]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchAuditLog(page, actionFilter);
  };

  // Client-side search filter on loaded entries
  const filteredEntries = searchText
    ? entries.filter(entry => {
        const term = searchText.toLowerCase();
        return (
          (entry.action && entry.action.toLowerCase().includes(term)) ||
          (entry.user_email && entry.user_email.toLowerCase().includes(term)) ||
          (entry.resource_type && entry.resource_type.toLowerCase().includes(term)) ||
          (entry.resource_id && entry.resource_id.toLowerCase().includes(term)) ||
          (entry.details && JSON.stringify(entry.details).toLowerCase().includes(term))
        );
      })
    : entries;

  const openDrawer = (entry: AuditLogEntry) => {
    setSelectedEntry(entry);
    setDrawerOpen(true);
  };

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (val: string) => formatTimestamp(val),
      width: 180,
    },
    {
      title: 'User',
      dataIndex: 'user_email',
      key: 'user_email',
      render: (email: string | null) => (email ? <Link to="/admin/users">{email}</Link> : '-'),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action: string) => <Tag color={actionTagColor(action)}>{action}</Tag>,
    },
    {
      title: 'Resource',
      key: 'resource',
      render: (_: any, record: AuditLogEntry) =>
        renderResourceLink(record.resource_type, record.resource_id),
    },
    {
      title: '',
      key: 'detail',
      width: 36,
      render: (_: any, record: AuditLogEntry) => (
        <InfoCircleOutlined
          style={{ cursor: 'pointer', color: '#1890ff' }}
          onClick={() => openDrawer(record)}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Audit Log"
        actions={
          <PageHelp
            title="Audit Log Help"
            sections={[
              {
                title: 'What is the Audit Log?',
                content:
                  'Every administrative action (creating users, allocating SIMs, modifying assets, etc.) is recorded here with who did it, when, and what changed.',
              },
              {
                title: 'Filtering',
                content:
                  'Use the action filter to narrow down to specific operation types like "create_user", "allocate_sim", etc.\n\nResults are sorted newest first.',
                tips: [
                  'Click on a row to see full details in JSON format',
                  'The log is read-only — entries cannot be deleted',
                ],
              },
            ]}
          />
        }
      />
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={14} md={10} lg={8}>
          <Search
            placeholder="Search entries..."
            allowClear
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </Col>
        <Col xs={24} sm={10} md={8} lg={6}>
          <Select
            placeholder="Filter by action"
            allowClear
            style={{ width: '100%' }}
            value={actionFilter}
            onChange={val => setActionFilter(val)}
            options={[
              { value: 'login', label: 'Login' },
              { value: 'create', label: 'Create' },
              { value: 'update', label: 'Update' },
              { value: 'delete', label: 'Delete' },
              { value: 'assign', label: 'Assign' },
              { value: 'unassign', label: 'Unassign' },
              { value: 'allocate', label: 'Allocate' },
              { value: 'deallocate', label: 'Deallocate' },
              { value: 'view', label: 'View' },
              { value: 'sim_activate', label: 'SIM Activate' },
              { value: 'sim_suspend', label: 'SIM Suspend' },
              { value: 'sim_resume', label: 'SIM Resume' },
              { value: 'sim_terminate', label: 'SIM Terminate' },
              { value: 'set_sim_visibility', label: 'Set SIM Visibility (KAN-38)' },
            ]}
          />
        </Col>
      </Row>
      <ResponsiveTableOrCards
        dataSource={filteredEntries}
        rowKey={record => record.id}
        columns={columns}
        loading={loading}
        breakpoint="xl"
        serverSidePagination={true}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: searchText ? filteredEntries.length : total,
          onChange: searchText ? undefined : handlePageChange,
          showTotal: (t: number) => `${t} entries`,
          showSizeChanger: false,
        }}
        renderCard={entry => <AuditLogCard entry={entry} onRowClick={openDrawer} />}
        emptyText="No audit entries found"
      />

      <Drawer
        title="Audit Log Entry"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedEntry(null);
        }}
        width={520}
      >
        {selectedEntry && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{selectedEntry.id}</Descriptions.Item>
            <Descriptions.Item label="Timestamp">
              {formatTimestamp(selectedEntry.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="User">
              {selectedEntry.user_email ? (
                <Link to="/admin/users">{selectedEntry.user_email}</Link>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Action">
              <Tag color={actionTagColor(selectedEntry.action)}>{selectedEntry.action}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Resource Type">
              {selectedEntry.resource_type || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Resource ID">
              {renderResourceLink(selectedEntry.resource_type, selectedEntry.resource_id)}
            </Descriptions.Item>
            <Descriptions.Item label="Details">
              {selectedEntry.details ? (
                <pre
                  style={{
                    margin: 0,
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    background: token.colorFillTertiary,
                    color: token.colorText,
                    padding: 8,
                    borderRadius: 4,
                  }}
                >
                  {JSON.stringify(selectedEntry.details, null, 2)}
                </pre>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="IP Address">
              {selectedEntry.ip_address || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};

export default AuditLog;
